import React, { FC, useEffect } from 'react';

import {
  adminActions,
  convertToDecimals,
  fallbackText,
  getPendingRefund,
  pendingRefund,
  RefundStatus,
} from '@pharmaplan/common';

import usePendingRefundActions from '../../../../hooks/Admin/PendingRefunds/usePendingRefundActions';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../../hooks/useAppSelector';
import CustomGridLayout from '../../../common/CustomGridLayout';
import ServiceCard from '../../../SelfService/ServiceCard';
import { getDayMonthDateYear } from '../../../Reports/helpers';
import strings from '../../../../localization';
import {
  setFooter,
  setFooterShow,
  setTitle,
} from '../../../../actions/drawerActions';

import { refundStatusLabelMap } from '../helper';
import styles from './styles';

interface IPendingRefundDetails {
  refundRequestId: string;
}

const successAction = adminActions.getPendingRefund;

const PendingRefundDetails: FC<IPendingRefundDetails> = ({
  refundRequestId,
}) => {
  const dispatch = useAppDispatch();

  const refund = useAppSelector(pendingRefund);
  const { approveAction, rejectRefundForm } = usePendingRefundActions();

  const {
    code,
    requestedDate,
    pharmacyName,
    reason,
    adminReason,
    status,
    subscriptionId,
    amount,
    actionDate,
  } = refund ?? {};

  const parsedDate = getDayMonthDateYear(requestedDate);
  const actionParsedDate = getDayMonthDateYear(actionDate ?? '');
  const isPending = status === RefundStatus.Pending;

  useEffect(() => {
    dispatch(getPendingRefund(refundRequestId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refundConfig = [
    { key: strings.requestId, value: code },
    {
      key: strings.formatString(
        strings.dateTimeSuffix,
        strings.requested,
      ) as string,
      value: parsedDate,
    },
    { key: strings.pharmacyName, value: pharmacyName },
    { key: strings.refundId, value: subscriptionId },
    { key: strings.amount, value: `$${convertToDecimals(amount ?? 0)}` },
    {
      key: strings.status,
      value: refundStatusLabelMap[status as keyof typeof refundStatusLabelMap],
    },
    {
      key: strings.formatString(
        strings.dateTimeSuffix,
        `${strings.approvedBy}/${strings.rejectedBy}`,
      ) as string,
      value: actionDate ? actionParsedDate : fallbackText,
    },
    {
      key: strings.formatString(strings.noteSuffix, strings.pharmacy) as string,
      value: reason,
    },
    {
      key: strings.formatString(strings.noteSuffix, strings.admin) as string,
      value: adminReason ?? '-',
    },
  ];

  const handleFooter = () => {
    dispatch(
      setFooter({
        show: true,
        pagination: {
          show: false,
        },
        buttons: {
          show: true,
          primaryButton: {
            label: strings.approve,
            onClick: approveAction(refundRequestId),
          },
          secondaryButton: {
            label: strings.reject,
            onClick: rejectRefundForm(refundRequestId),
          },
        },
      }),
    );
  };

  useEffect(() => {
    const title = `${strings.code}: ${code}`;
    dispatch(setTitle({ title }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    if (isPending) {
      handleFooter();
    } else {
      dispatch(setFooterShow(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  return (
    <ServiceCard
      customContainerStyle={styles.serviceContainer}
      content={(
        <CustomGridLayout
          successAction={successAction}
          title={strings.requestInfo}
          config={refundConfig}
        />
      )}
    />
  );
};

export default PendingRefundDetails;
